import { apiClient } from '../lib/apiClient';
import type { Confidence, LightGroup } from './lightSurveyService';
import type { FlowBand } from './flowSurveyService';

/**
 * Placement assistant (flow module phase 4): the best spots for a new coral from
 * the light map and the flow map together (src/services/placement), and the AI's
 * choice among them.
 */
export interface PlacementCandidate {
  readonly rank: number;
  readonly kind: 'spot' | 'zone';
  readonly x: number | null;
  readonly y: number | null;
  readonly zone: FlowBand | null;
  readonly near: readonly string[];
  readonly par: number | null;
  readonly lightConfidence: Confidence | null;
  readonly flowMin: number | null;
  readonly flowMax: number | null;
  readonly flowConfidence: Confidence | null;
  readonly fitsLight: boolean;
  readonly fitsFlow: boolean;
  readonly occupiedBy: string | null;
  readonly score: number;
}

export type PlacementMode = 'same_photo' | 'zones' | 'light_only' | 'flow_only' | 'no_data';

export interface Placement {
  readonly group: LightGroup;
  readonly name: string | null;
  readonly mode: PlacementMode;
  readonly candidates: readonly PlacementCandidate[];
  readonly need: { readonly minPar: number | null; readonly maxPar: number | null; readonly peakMin: number; readonly peakMax: number; readonly troughMax: number };
  readonly lightSurvey: { readonly id: string; readonly title: string; readonly measuredAt: string } | null;
  readonly flowSurvey: { readonly id: string; readonly title: string; readonly measuredAt: string } | null;
  readonly imageUrl: string | null;
  readonly imageWidth: number | null;
  readonly imageHeight: number | null;
}

export type AdviceVerdict = 'recommended' | 'possible' | 'avoid';

export interface PlacementAdvice {
  readonly summary: string;
  readonly picks: ReadonlyArray<{ readonly rank: number; readonly verdict: AdviceVerdict; readonly reason: string }>;
  readonly cautions: readonly string[];
  readonly acclimation: string;
  readonly language: string;
  readonly usedPhoto: boolean;
}

export interface PlacementQuery {
  group?: LightGroup;
  name?: string;
}

const base = (aquariumId: string) => `/api/v1/aquariums/${aquariumId}/coral-placement`;
export const placementKey = (aquariumId: string, q: PlacementQuery) => ['coral-placement', aquariumId, q.group ?? null, q.name ?? null] as const;

export async function getPlacement(aquariumId: string, q: PlacementQuery): Promise<Placement> {
  const params = new URLSearchParams();
  if (q.group) params.set('group', q.group);
  if (q.name) params.set('name', q.name);
  const res = await apiClient.get<{ data: Placement }>(`${base(aquariumId)}?${params.toString()}`);
  return res.data;
}

export async function getPlacementAdvice(aquariumId: string, q: PlacementQuery): Promise<{ placement: Placement; advice: PlacementAdvice }> {
  const res = await apiClient.post<{ data: { placement: Placement; advice: PlacementAdvice } }>(`${base(aquariumId)}/advice`, q);
  return res.data;
}
